import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { pedidoService, Pedido } from '../../services/pedido';

// Interfaces para corresponder à estrutura de retorno da API
interface PedidoDetalhe extends Pedido {
  pagamentos: PagamentoDetalhe[];
  produtos: ProdutoDetalhe[];
}

interface PagamentoDetalhe {
  id: number;
  idPedido: number;
  idFormaPagamento: number;
  formaPagamento: string;
  valor: number;
  dataPagamento: string;
}

interface ProdutoDetalhe {
  id: number;
  idPedido: number;
  nomeProduto: string;
  referencia: string;
  precoUnitario: number;
  valorTotal: number;
  quantidade: number;
  grade: string;
}

const PedidoVisualizar: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data: pedido, isLoading, error } = useQuery({
    queryKey: ['pedido', id],
    queryFn: () => pedidoService.getById(Number(id)),
    enabled: !!id,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getFreteTypeColor = (tipo: string) => {
    switch (tipo?.toUpperCase()) {
      case 'CIF':
        return 'primary';
      case 'FOB':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !pedido) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar o pedido.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Pedido #{pedido.id}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Fornecedor: {pedido.nomeFornecedor}
        </Typography>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Dados Principais */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dados do Pedido
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Código
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  #{pedido.id}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Fornecedor
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {pedido.nomeFornecedor}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Quantidade de Itens
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {pedido.quantidade}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Datas */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Datas
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data do Pedido
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {pedido.dataPedidoString}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data Prevista Entrega
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {pedido.dataPrevistaEntregaString}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Informações de Frete */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <ShippingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Informações de Frete
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Tipo de Frete
                </Typography>
                <Chip
                  label={pedido.tipoFrete}
                  color={getFreteTypeColor(pedido.tipoFrete)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Valor do Frete
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {formatCurrency(pedido.valorFrete)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Valores */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Valores
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Valor do Pedido
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {formatCurrency(pedido.valorPedido)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Produtos */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Produtos do Pedido
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Preço Unitário</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Grade</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(pedido as any)?.produtos && (pedido as any).produtos.length > 0 ? (
                  (pedido as any).produtos.map((produto: any) => (
                    <TableRow key={produto.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {produto.referencia}
                      </TableCell>
                      <TableCell>{produto.nomeProduto}</TableCell>
                      <TableCell>{produto.quantidade}</TableCell>
                      <TableCell>{formatCurrency(produto.precoUnitario)}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(produto.valorTotal)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {produto.grade || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum produto encontrado neste pedido.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagamentos */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Pagamentos
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Forma de Pagamento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Pagamento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(pedido as any)?.pagamentos && (pedido as any).pagamentos.length > 0 ? (
                  (pedido as any).pagamentos.map((pagamento: any) => (
                    <TableRow key={pagamento.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={pagamento.formaPagamento}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(pagamento.valor)}
                      </TableCell>
                      <TableCell>
                        {pagamento.dataPagamento ? 
                          new Date(pagamento.dataPagamento).toLocaleDateString('pt-BR') 
                          : '-'
                        }
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum pagamento encontrado neste pedido.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PedidoVisualizar;
